/**
 * THE event status badge. Every surface that shows an event's status renders this,
 * so the label, the colour and — above all — the answer to "is this happening right
 * now?" cannot drift between the list, the detail page and the MSR portal.
 *
 * The liveness decision itself lives in utils/eventLiveStatus (unit-tested there);
 * this component only renders it and keeps it fresh via the shared useNowTick clock,
 * so a badge on a page left open all evening flips to "Happening now" and back to
 * "Ended" on its own.
 */

import {
  deriveEventDisplayStatus,
  type EventLivenessInput,
} from "../../utils/eventLiveStatus";
import { useNowTick } from "../../hooks/useNowTick";

export interface EventStatusBadgeProps {
  /** Any row carrying status + start/end/timezone/all_day/date_tbd. */
  event: EventLivenessInput;
  /** Extra classes appended after the badge's own layout + colour. */
  className?: string;
  /**
   * Render nothing when the derived badge would just repeat the stored status.
   * For surfaces that ALREADY show the stored status through another control —
   * the event detail page's status <select> — so the badge appears only when it
   * has something the stored value does not say. The condition is re-evaluated
   * on every tick, so the badge appears the moment an event goes live.
   */
  onlyWhenDerived?: boolean;
  /**
   * Fixed evaluation instant. Tests only — production always uses the live clock
   * so an open page keeps telling the truth.
   */
  now?: Date | number;
  /** Show a past stored-'scheduled' event as "Ended" (MSR Past tab). Off everywhere else. */
  pastScheduledAsEnded?: boolean;
}

export default function EventStatusBadge({
  event,
  className = "",
  onlyWhenDerived = false,
  now,
  pastScheduledAsEnded = false,
}: EventStatusBadgeProps) {
  // Subscribe unconditionally (hook order); the value is ignored when `now` is fixed.
  const tick = useNowTick();
  const display = deriveEventDisplayStatus(event, now ?? tick, {
    pastScheduledAsEnded,
  });

  if (onlyWhenDerived && !display.derived) {
    return null;
  }

  return (
    <span
      data-testid="event-status-badge"
      data-status-key={display.key}
      data-stored-status={display.storedStatus}
      title={display.title}
      className={`px-2 py-0.5 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${display.className} ${className}`}
    >
      {display.label}
    </span>
  );
}
